/**
 * 视频时长选择器
 *
 * 根据 DurationSpec 类型自动切换渲染模式：
 * - enum：按钮组
 * - range：滑动条
 */

import type { DurationSpec } from '../services/videoApi';

interface VideoDurationSelectorProps {
  value: number;
  onChange: (value: number) => void;
  /** 兼容旧接口：传 number[] 时按 enum 处理 */
  options?: number[];
  /** 新接口：传 DurationSpec 对象 */
  spec?: DurationSpec;
  /**
   * 紧凑尺寸：画布节点等小容器内使用。
   * 标签与按钮对齐 text-[10px] 标签 + text-xs 下拉的紧凑控件规格，
   * 选中态用紫色系（画布生视频节点主题色）。
   */
  compact?: boolean;
}

export function VideoDurationSelector({ value, onChange, options, spec, compact }: VideoDurationSelectorProps) {
  // 兼容：如果传了 options 但没传 spec，转成 enum spec
  const resolvedSpec: DurationSpec = spec
    ? spec
    : options && options.length > 0
      ? { type: 'enum', values: options }
      : { type: 'enum', values: [] };

  if (resolvedSpec.type === 'enum') {
    if (resolvedSpec.values.length === 0) return null;

    return (
      <div>
        <div className={compact ? 'mb-0.5 block text-[10px] text-slate-400' : 'text-sm font-medium text-light-text mb-2'}>时长</div>
        <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-2'}`}>
          {resolvedSpec.values.map((sec) => {
            const isActive = sec === value;
            return (
              <button
                key={sec}
                type="button"
                onClick={() => onChange(sec)}
                className={
                  compact
                    ? `rounded-md border px-2 py-1 text-xs font-medium transition-all ${
                        isActive
                          ? 'border-purple-400 bg-purple-500 text-white shadow-sm'
                          : 'border-light-border bg-white text-slate-500 hover:border-purple-400 hover:text-purple-600'
                      }`
                    : `px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                        isActive
                          ? 'border-accent-blue bg-accent-blue text-white shadow-md'
                          : 'border-light-border bg-white text-light-secondary hover:border-accent-blue hover:text-accent-blue'
                      }`
                }
              >
                {sec}s
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // range 模式：滑动条
  const { min, max, step } = resolvedSpec;

  return (
    <div>
      <div
        className={
          compact
            ? 'mb-0.5 block text-[10px] text-slate-400'
            : 'text-sm font-medium text-light-text mb-2'
        }
      >
        时长{' '}
        <span className={compact ? 'ml-1 font-medium text-purple-600' : 'text-accent-blue font-semibold ml-1'}>
          {value}s
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className={compact ? 'w-6 text-right text-[10px] text-slate-400' : 'text-xs text-light-muted w-8 text-right'}>{min}s</span>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`flex-1 cursor-pointer appearance-none rounded-lg bg-light-border accent-accent-blue ${compact ? 'h-1.5' : 'h-2'}`}
        />
        <span className={compact ? 'w-6 text-[10px] text-slate-400' : 'text-xs text-light-muted w-8'}>{max}s</span>
      </div>
    </div>
  );
}
